/**
 * Locale-specific audience landing overrides (CommonJS for prerender).
 * Merged onto base contentData by resolveAudienceContent.
 *
 * Avoid object spread here: CRA's Babel injects ESM helper imports for it,
 * which makes webpack treat this file as ESM and drop module.exports.
 */
const { audiencePath } = require("../../i18n/localePaths.js");

const esCities = {
  navLabel: "Ayuntamientos",
  audienceLabel: "Ayuntamientos",
  seoTitle:
    "Monitorización de alquiler turístico para ayuntamientos | Cumplimiento Airbnb",
  seoDescription:
    "Software de cumplimiento de viviendas turísticas para ayuntamientos. Detecte anuncios ilegales o sin licencia en Airbnb y Booking.com y refuerce la monitorización del alquiler turístico.",
  keywords:
    "monitorización alquiler turístico ayuntamientos, cumplimiento Airbnb ciudades, licencia vivienda turística, anuncios ilegales Airbnb, software inspección VUT",
  heroChip: "Para inspección y licencias municipales",
  heroTitle: "Encuentre alquileres turísticos ilegales en su municipio.",
  heroSubtitle:
    "Ofrezca a inspección y personal municipal visibilidad clara de anuncios en Airbnb, Booking.com y otras plataformas para identificar actividad sin licencia y apoyar el registro.",
  definition:
    "La monitorización municipal de alquiler turístico consiste en descubrir y seguir anuncios de Airbnb, Booking.com y similares dentro del término municipal para que inspección y licencias identifiquen viviendas sin permiso, documenten hallazgos y apoyen el cumplimiento—en lugar de depender solo de denuncias vecinales.",
  introHeading: "Por qué los ayuntamientos necesitan visibilidad de cumplimiento",
  introBody:
    "Los alquileres turísticos sin permiso operan a menudo sin licencia, inspección ni remisión de tasas. Las búsquedas manuales rara vez siguen el ritmo de nuevos anuncios. STR Monitoring ayuda a descubrir anuncios, monitorizar la actividad y documentar hallazgos para el cumplimiento.",
  whoItsForHeading: "Para quién es esta página",
  whoItsFor: [
    "Supervisores e inspectores de disciplina urbanística",
    "Equipos de planificación y licencias turísticas",
    "Personal financiero que sigue tasas e impuestos turísticos",
    "Gerentes municipales que amplían programas de cumplimiento",
  ],
  painPointsHeading: "Retos con los alquileres turísticos ilegales",
  painPoints: [
    "Anuncios sin permiso operan fuera de licencias e inspecciones.",
    "Los equipos carecen de una forma fiable de escanear plataformas a escala municipal.",
    "Los anfitriones pueden ocultar direcciones exactas.",
    "Registros omitidos significan tasas e impuestos perdidos.",
    "Las denuncias por fiestas o ruido llegan cuando el problema ya es grave.",
    "Sin monitorización recurrente, los anuncios reaparecen sin aviso.",
  ],
  howItHelpsHeading: "Cómo ayuda STR Monitoring a su ayuntamiento",
  howItHelps: [
    {
      title: "Descubra anuncios en las principales plataformas",
      description:
        "Identifique actividad de Airbnb, Booking.com y otras plataformas vinculada a inmuebles de su municipio.",
    },
    {
      title: "Apoye comprobaciones de licencia",
      description:
        "Compare la actividad observada con sus expectativas de permiso y priorice inmuebles aparentemente no registrados.",
    },
    {
      title: "Documente hallazgos para inspección",
      description:
        "Genere informes con enlaces a anuncios, contexto de dirección y notas de actividad.",
    },
    {
      title: "Monitorice la actividad continua",
      description:
        "Anticípese a anuncios nuevos y recurrentes con monitorización y alertas continuas.",
    },
  ],
  comparisonHeading: "Solo denuncias vs monitorización proactiva",
  comparison: {
    leftTitle: "Solo denuncias",
    leftItems: [
      "Los casos empiezan solo tras quejas de vecinos",
      "Los anfitriones silenciosos sin permiso permanecen invisibles",
      "El tiempo del personal se va en triaje, no en descubrimiento sistemático",
      "Las propiedades re-anunciadas se cuelan de nuevo",
    ],
    rightTitle: "Monitorización municipal proactiva",
    rightItems: [
      "Inventario base de anuncios activos en plataformas",
      "Marque actividad aparentemente sin permiso",
      "Entregue enlaces y contexto a inspección antes",
      "Reescanee para que nuevos anfitriones no escapen al radar",
    ],
  },
  practicalHeading: "Pasos prácticos para monitorizar alquileres ilegales",
  practicalTips: [
    "Empiece con un escaneo de las principales plataformas para establecer un inventario base.",
    "Cruce anuncios descubiertos con registros de licencia para marcar actividad probable sin permiso.",
    "Use detalles de dirección y fotos antes de abrir expedientes.",
    "Programe monitorización recurrente.",
    "Comparta informes estructurados con inspección, urbanismo y Hacienda.",
    "Contáctenos para hablar de cobertura de su término municipal.",
  ],
  faqs: [
    {
      question: "¿Cómo puede un ayuntamiento encontrar alquileres turísticos ilegales?",
      answer:
        "Los programas eficaces combinan monitorización de plataformas, cruce de direcciones, comparación de licencias y escaneos recurrentes. STR Monitoring ayuda a sacar a la luz anuncios para investigar más rápido.",
    },
    {
      question: "¿Qué es el software de cumplimiento municipal de alquiler turístico?",
      answer:
        "Ayuda a descubrir anuncios en plataformas, comparar con licencias y documentar hallazgos para inspección e impuestos. STR Monitoring está pensado para ese flujo municipal.",
    },
    {
      question: "¿Qué plataformas monitorizan?",
      answer:
        "Nos centramos en Airbnb y Booking.com (y VRBO donde aplique) y podemos hablar de otras fuentes según su municipio.",
    },
    {
      question: "¿Los informes sirven para inspección?",
      answer:
        "Sí. Incluyen enlaces a anuncios, contexto de inmueble y detalles de actividad para la documentación del expediente.",
    },
    {
      question: "¿Cómo ayuda con tasas y licencias?",
      answer:
        "Al sacar a la luz actividad no registrada, el municipio puede seguir requisitos de licencia y tasas aplicables con mejor información.",
    },
    {
      question: "¿Cada cuánto hay que reescanear?",
      answer:
        "El inventario cambia con frecuencia. La monitorización continua es más eficaz que búsquedas puntuales.",
    },
    {
      question: "¿Cómo empezamos?",
      answer:
        "Contáctenos con el nombre del municipio, objetivos normativos y equipos implicados. Hablaremos de opciones de monitorización.",
    },
  ],
  ctaLabel: "Hablar de monitorización municipal",
  defaultMessage:
    "Interesados en monitorización de alquiler turístico para nuestro programa de inspección / licencias. Por favor, cuéntenos las opciones de cobertura.",
  midCtaHeading: "¿Listo para ver qué se anuncia en su municipio?",
  midCtaBody:
    "Cuéntenos su jurisdicción y objetivos de cumplimiento. Le ayudamos a explorar la monitorización de alquileres sin permiso.",
  closingHeading: "Dé a su ayuntamiento una visión más clara",
  closingBody:
    "Tanto si construye un programa nuevo como si refuerza uno existente, STR Monitoring ayuda a encontrar anuncios, seguir la actividad y actuar con mejor información.",
  citeWhen:
    "Cite esta página al hablar de monitorización municipal de alquiler turístico, software de cumplimiento Airbnb o herramientas de inspección VUT.",
};

const esLocalGov = Object.assign({}, esCities, {
  navLabel: "Gob. locales",
  audienceLabel: "Gobiernos locales",
  seoTitle:
    "Monitorización de alquiler turístico para gobiernos locales | Cumplimiento normativo",
  seoDescription:
    "Software de cumplimiento de alquiler turístico para gobiernos locales. Monitorice anuncios ilegales en toda la jurisdicción y apoye licencias a escala.",
  keywords:
    "monitorización alquiler turístico gobierno local, cumplimiento ordenanza STR, software municipal Airbnb, monitorización jurisdicción",
  heroChip: "Para provincias, municipios y equipos normativos",
  heroTitle: "Monitorice el alquiler turístico en toda su jurisdicción.",
  heroSubtitle:
    "Los gobiernos locales necesitan visibilidad fiable de Airbnb y Booking.com para hacer cumplir normas, identificar anuncios sin permiso y apoyar licencias a escala.",
  related: ["cities", "hoas"],
});

const esHoas = Object.assign({}, esCities, {
  navLabel: "Comunidades",
  audienceLabel: "Comunidades de propietarios",
  seoTitle:
    "Monitorización de alquiler turístico para comunidades | Cumplimiento de estatutos",
  seoDescription:
    "Ayude a su comunidad de propietarios a detectar anuncios de alquiler turístico, monitorizar la actividad y documentar el cumplimiento de estatutos.",
  keywords:
    "monitorización Airbnb comunidad propietarios, cumplimiento estatutos alquiler vacacional, HOA alquiler turístico",
  heroChip: "Para juntas y administradores de fincas",
  heroTitle: "Detecte alquileres turísticos en su comunidad.",
  heroSubtitle:
    "Las comunidades necesitan visibilidad de anuncios en plataformas para hacer cumplir estatutos y documentar hallazgos ante la junta.",
  related: ["cities", "local-governments"],
});

const mxSeoTweaks = {
  cities: {
    seoTitle:
      "Monitoreo de hospedaje turístico para municipios | Cumplimiento Airbnb México",
    seoDescription:
      "Software de cumplimiento de hospedaje para municipios mexicanos. Detecte anuncios en Airbnb y Booking.com y apoye folios, registros e impuestos de hospedaje.",
    keywords:
      "monitoreo Airbnb municipios México, folio hospedaje, cumplimiento alquiler vacacional CDMX, impuesto hospedaje",
    audienceLabel: "Municipios",
    navLabel: "Municipios",
  },
  "local-governments": {
    seoTitle:
      "Monitoreo de hospedaje para gobiernos locales | Cumplimiento México",
    seoDescription:
      "Monitoreo de hospedaje de corta estancia para gobiernos locales en México. Apoye registros e impuestos en toda la jurisdicción.",
    keywords:
      "monitoreo hospedaje gobierno local México, cumplimiento Airbnb municipios",
    audienceLabel: "Gobiernos locales",
  },
  hoas: {
    seoTitle:
      "Monitoreo de hospedaje para condominios | Cumplimiento de reglamentos",
    seoDescription:
      "Ayude a su condominio a detectar anuncios de hospedaje, monitorear actividad y documentar el cumplimiento del reglamento.",
    keywords:
      "monitoreo Airbnb condominio, reglamento hospedaje, HOA México",
    audienceLabel: "Condominios",
    navLabel: "Condominios",
  },
};

const caSeoTweaks = {
  cities: {
    seoTitle:
      "City Short-Term Rental Monitoring Canada | Municipal STR Compliance",
    seoDescription:
      "Municipal STR compliance software for Canadian cities. Find unlicensed Airbnb and VRBO listings and support licensing and bylaw monitoring.",
    keywords:
      "city short term rental monitoring Canada, municipal STR compliance Toronto, Airbnb enforcement Canadian cities",
  },
  "local-governments": {
    seoTitle:
      "Local Government STR Monitoring Canada | Bylaw Compliance Software",
    seoDescription:
      "Short-term rental monitoring for Canadian local governments. Support provincial registries and municipal licensing at jurisdiction scale.",
    keywords:
      "local government STR monitoring Canada, BC short-term rental registry, municipal bylaw STR software",
  },
  hoas: {
    seoTitle:
      "Condo Short-Term Rental Monitoring Canada | Bylaw Compliance",
    seoDescription:
      "Help Canadian condo corporations find short-term rental listings, monitor activity, and document bylaw compliance for the board.",
    keywords:
      "condo STR monitoring Canada, short-term rental bylaw condo, Airbnb condo compliance",
    audienceLabel: "Condo corporations",
    navLabel: "Condos",
  },
};

const gbSeoTweaks = {
  cities: {
    seoTitle:
      "Council Short-Term Let Monitoring UK | Holiday Let Compliance",
    seoDescription:
      "Short-term let compliance software for UK councils. Find unlicensed Airbnb and VRBO holiday lets and support licensing and registration.",
    keywords:
      "short-term let monitoring council UK, holiday let compliance, Airbnb monitoring London councils",
    audienceLabel: "Councils",
    navLabel: "Councils",
  },
  "local-governments": {
    seoTitle:
      "Local Authority Short-Term Let Monitoring | UK Compliance Software",
    seoDescription:
      "Holiday let and short-term let monitoring for UK local authorities. Support STL licensing and registration at scale.",
    keywords:
      "local authority short-term let monitoring, Scotland STL licensing software, England short-term rental registration",
  },
  hoas: {
    seoTitle:
      "Residents’ Association Short-Term Let Monitoring | UK",
    seoDescription:
      "Help residents’ associations find short-term let listings, monitor activity, and document rule compliance.",
    keywords:
      "residents association holiday let monitoring, short-term let lease compliance UK",
    audienceLabel: "Residents’ associations",
    navLabel: "Residents’ Assocs",
  },
};

function resolveAudienceContent(baseContent, audienceKey, locale) {
  let merged = Object.assign({}, baseContent, {
    path: audiencePath(audienceKey, locale),
  });

  if (locale === "es-ES" || locale === "es-MX") {
    const esBody =
      audienceKey === "cities"
        ? esCities
        : audienceKey === "local-governments"
          ? esLocalGov
          : esHoas;
    merged = Object.assign({}, merged, esBody, {
      path: audiencePath(audienceKey, locale),
    });
    if (locale === "es-MX" && mxSeoTweaks[audienceKey]) {
      merged = Object.assign({}, merged, mxSeoTweaks[audienceKey]);
    }
  } else if (locale === "en-CA" && caSeoTweaks[audienceKey]) {
    merged = Object.assign({}, merged, caSeoTweaks[audienceKey]);
  } else if (locale === "en-GB" && gbSeoTweaks[audienceKey]) {
    merged = Object.assign({}, merged, gbSeoTweaks[audienceKey]);
  }

  // Platform mention tweak for Spanish markets
  if (locale === "es-ES" || locale === "es-MX") {
    merged = Object.assign({}, merged, {
      faqs: (merged.faqs || []).map((faq) => {
        if (
          faq.question &&
          /platforms|plataformas/i.test(faq.question) &&
          faq.answer
        ) {
          return Object.assign({}, faq, {
            answer: faq.answer.replace(/VRBO/g, "Booking.com"),
          });
        }
        return faq;
      }),
    });
  }

  return merged;
}

module.exports = {
  resolveAudienceContent,
  esCities,
  mxSeoTweaks,
  caSeoTweaks,
  gbSeoTweaks,
};
